// @ts-nocheck
// Inbox de cutucadas (pokes) recebidas pelo viewer.
// Sheet bottom-up com lista de cutucadas. Ao abrir marca tudo como lido
// (agressivo intencionalmente: o user já viu = "lido"; mantém badge limpa).
import React, { useEffect, useState } from 'react';
import { Sheet } from '../common';
import { Avatar } from './components';
import { Icon } from '../icons';
import { api } from '../../services/apiV2';

const POKE_TYPE_LABEL: Record<string, { icon: string; label: string }> = {
  general:           { icon: '👋', label: 'cutucou você' },
  'milestone-late':  { icon: '🎯', label: 'lembrou do marco' },
  'pause-noticed':   { icon: '🌙', label: 'sentiu sua falta' },
};

export const PokesSheet: React.FC<{
  open: boolean;
  onClose: () => void;
  session: any;
  onAfterRead?: () => void; // chamado depois que o sheet marca tudo como lido
  onOpenClub?: (clubId: string) => void;
}> = ({ open, onClose, session, onAfterRead, onOpenClub }) => {
  const [pokes, setPokes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !session) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await api.listPokesInbox(session);
        if (cancelled) return;
        setPokes(r.pokes || []);
        // Ao abrir, marca tudo como lido (badge zera) — sem await, fire-and-forget
        if ((r.pokes || []).some((p: any) => !p.read)) {
          api.markAllPokesRead(session)
            .then(() => onAfterRead?.())
            .catch(() => { /* silencioso */ });
        }
      } catch (e) {
        console.error('[pokes/inbox]', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, session, onAfterRead]);

  return (
    <Sheet open={open} onClose={onClose} title="Cutucadas recebidas">
      <div style={{ paddingBottom: 8 }}>
        {loading && pokes.length === 0 && (
          <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
            Carregando…
          </div>
        )}
        {!loading && pokes.length === 0 && (
          <div style={{
            padding: 24, textAlign: 'center',
            border: '1px dashed var(--line-strong)', borderRadius: 14,
            color: 'var(--ink-3)', fontSize: 13,
          }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>👋</div>
            <div className="serif" style={{ fontSize: 16, fontStyle: 'italic', marginBottom: 4 }}>
              Nenhuma cutucada por aqui
            </div>
            <div style={{ fontSize: 12 }}>
              Quando algum líder do seu clube cutucar, aparece aqui.
            </div>
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {pokes.map((p: any) => {
            const meta = POKE_TYPE_LABEL[p.type] || POKE_TYPE_LABEL.general;
            return (
              <button
                key={p.id}
                onClick={() => {
                  if (p.clubId && onOpenClub) {
                    onOpenClub(p.clubId);
                    onClose();
                  }
                }}
                style={{
                  width: '100%', textAlign: 'left',
                  padding: 12, borderRadius: 14,
                  background: p.read ? 'var(--paper-2)' : 'color-mix(in oklab, var(--ember) 8%, var(--paper-2))',
                  border: `1px solid ${p.read ? 'var(--line)' : 'var(--ember)'}`,
                  display: 'flex', alignItems: 'center', gap: 12,
                  cursor: 'pointer',
                  transition: 'background 160ms',
                }}
              >
                <Avatar letter={p.from?.avatar || '?'} size={40}/>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>{p.from?.name || 'Membro'}</span>
                    <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>· {p.timeAgo}</span>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.4 }}>
                    {meta.icon} {meta.label}
                    {p.clubName && (
                      <span style={{ color: 'var(--ink-3)' }}> em <i>{p.clubName}</i></span>
                    )}
                  </div>
                  {p.message && (
                    <div className="serif" style={{
                      marginTop: 4, fontSize: 13, fontStyle: 'italic', color: 'var(--ink-2)',
                      paddingLeft: 8, borderLeft: '2px solid var(--line)',
                    }}>"{p.message}"</div>
                  )}
                </div>
                <Icon name="chevron-down" size={14} color="var(--ink-4)"/>
              </button>
            );
          })}
        </div>
      </div>
    </Sheet>
  );
};

// Ícone sininho com badge contendo count de cutucadas não-lidas.
// Click chama onOpen.
export const PokesBell: React.FC<{
  unread: number;
  onOpen: () => void;
}> = ({ unread, onOpen }) => (
  <button
    onClick={onOpen}
    aria-label={`Cutucadas (${unread} não-lidas)`}
    style={{
      width: 36, height: 36, borderRadius: '50%',
      background: 'var(--paper-2)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: '1px solid var(--line)', position: 'relative',
      cursor: 'pointer',
    }}
  >
    <span style={{ fontSize: 16 }}>👋</span>
    {unread > 0 && (
      <span style={{
        position: 'absolute', top: -2, right: -2,
        minWidth: 16, height: 16, padding: '0 4px',
        borderRadius: 8, background: 'var(--ember)',
        color: 'var(--paper)', fontSize: 9.5, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '2px solid var(--paper)',
      }}>{unread > 9 ? '9+' : unread}</span>
    )}
  </button>
);
